import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, User as UserIcon, Lock, Trash2, AlertTriangle, Save, Check, LogOut, CreditCard, ExternalLink, ShieldCheck, Download, KeyRound, Smartphone } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AccountPage() {
  const { user, refresh } = useAuth();
  const initials = (user?.name || user?.email || "U").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();

  return (
    <DashboardLayout
      eyebrow="Account"
      title="Account settings"
      subtitle="Manage your profile, password and account."
    >
      <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-6 mb-6 flex items-center gap-5" data-testid="account-summary">
        <Avatar className="w-16 h-16 rounded-sm">
          <AvatarImage src={user?.picture} />
          <AvatarFallback className="bg-[#0055FF]/20 text-[#3377FF] rounded-sm text-lg font-display">{initials}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display text-2xl font-medium">{user?.name || "—"}</span>
            <Badge variant="outline" className="rounded-sm border-zinc-700 text-zinc-400 uppercase text-[10px]">{user?.auth_provider}</Badge>
            {user?.role === "admin" && (
              <Badge className="rounded-sm bg-[#F59E0B]/15 text-[#F59E0B] border-[#F59E0B]/30">Admin</Badge>
            )}
          </div>
          <p className="text-sm text-zinc-500 mt-1">{user?.email}</p>
          <p className="text-xs text-zinc-600 mt-1.5">Joined {user?.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}</p>
        </div>
      </div>

      <Tabs defaultValue="profile">
        <TabsList className="bg-zinc-950 border border-zinc-800 rounded-sm" data-testid="account-tabs">
          <TabsTrigger value="profile" className="rounded-sm data-[state=active]:bg-[#0055FF] data-[state=active]:text-white" data-testid="tab-profile">
            <UserIcon className="w-3.5 h-3.5 mr-1.5" /> Profile
          </TabsTrigger>
          <TabsTrigger value="password" className="rounded-sm data-[state=active]:bg-[#0055FF] data-[state=active]:text-white" data-testid="tab-password">
            <Lock className="w-3.5 h-3.5 mr-1.5" /> Password
          </TabsTrigger>
          <TabsTrigger value="billing" className="rounded-sm data-[state=active]:bg-[#0055FF] data-[state=active]:text-white" data-testid="tab-billing">
            <CreditCard className="w-3.5 h-3.5 mr-1.5" /> Billing
          </TabsTrigger>
          <TabsTrigger value="security" className="rounded-sm data-[state=active]:bg-[#0055FF] data-[state=active]:text-white" data-testid="tab-security">
            <ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> Security
          </TabsTrigger>
          <TabsTrigger value="privacy" className="rounded-sm data-[state=active]:bg-[#0055FF] data-[state=active]:text-white" data-testid="tab-privacy">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Privacy
          </TabsTrigger>
          <TabsTrigger value="danger" className="rounded-sm data-[state=active]:bg-[#EF4444] data-[state=active]:text-white" data-testid="tab-danger">
            <AlertTriangle className="w-3.5 h-3.5 mr-1.5" /> Danger zone
          </TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-4">
          <ProfileSection user={user} onSaved={refresh} />
        </TabsContent>
        <TabsContent value="password" className="mt-4">
          <PasswordSection authProvider={user?.auth_provider} />
        </TabsContent>
        <TabsContent value="billing" className="mt-4">
          <BillingSection />
        </TabsContent>
        <TabsContent value="security" className="mt-4">
          <TwoFASection />
        </TabsContent>
        <TabsContent value="privacy" className="mt-4">
          <PrivacySection />
        </TabsContent>
        <TabsContent value="danger" className="mt-4">
          <DangerSection authProvider={user?.auth_provider} role={user?.role} />
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}

function TwoFASection() {
  const [status, setStatus] = useState(null);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/2fa/status");
      setStatus(data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/2fa/setup");
      setSetup(data);
    } catch (e) {
      alert(e?.response?.data?.detail || "Setup failed");
    } finally { setBusy(false); }
  };

  const verify = async () => {
    if (!code) return;
    setBusy(true);
    try {
      const { data } = await api.post("/2fa/verify", { code });
      setBackupCodes(data.backup_codes);
      setSetup(null);
      setCode("");
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Verification failed");
    } finally { setBusy(false); }
  };

  const disable = async () => {
    const c = window.prompt("Enter your current 2FA code (or leave blank to use password):");
    const password = c ? null : window.prompt("Enter your account password to disable 2FA:");
    if (!c && !password) return;
    setBusy(true);
    try {
      await api.post("/2fa/disable", { code: c || null, password: password || null });
      setBackupCodes(null);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Disable failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="account-2fa-section">
      <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-6">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-10 h-10 rounded-sm bg-[#10B981]/15 border border-[#10B981]/40 grid place-items-center">
            <Smartphone className="w-5 h-5 text-[#10B981]" />
          </div>
          <div className="flex-1">
            <h3 className="font-display font-medium">Two-factor authentication (2FA)</h3>
            <p className="text-xs text-zinc-500 mt-1 leading-relaxed">Add an extra layer of security. Use Google Authenticator, Authy, 1Password, or any TOTP app.</p>
          </div>
          {status?.enabled ? (
            <Badge className="bg-[#10B981]/10 text-[#10B981] border-[#10B981]/30 rounded-sm">Enabled</Badge>
          ) : (
            <Badge variant="outline" className="rounded-sm border-zinc-700 text-zinc-500">Disabled</Badge>
          )}
        </div>

        {!status?.enabled && !setup && (
          <Button onClick={startSetup} disabled={busy} className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm" data-testid="2fa-start">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />} Enable 2FA
          </Button>
        )}

        {setup && (
          <div className="space-y-4">
            <div className="text-sm text-zinc-300">1. Scan this QR with your authenticator app:</div>
            <div className="inline-block bg-white p-3 rounded-sm">
              <img src={setup.qr_data_url} alt="2FA QR Code" className="w-40 h-40" data-testid="2fa-qr" />
            </div>
            <div className="text-xs text-zinc-500">Or enter this secret manually: <code className="text-[#3377FF] font-mono">{setup.secret}</code></div>
            <div>
              <Label className="text-[10px] tracking-widest uppercase text-zinc-500 mb-1.5 block">2. Enter the 6-digit code from your app:</Label>
              <div className="flex gap-2">
                <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" maxLength={6} className="bg-zinc-950 border-zinc-800 rounded-sm h-10 max-w-[140px] font-mono" data-testid="2fa-code" />
                <Button onClick={verify} disabled={busy || code.length < 6} className="bg-[#10B981] hover:bg-[#10B981]/90 text-[#062D24] rounded-sm" data-testid="2fa-verify">
                  {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />} Verify & enable
                </Button>
              </div>
            </div>
          </div>
        )}

        {backupCodes && (
          <div className="mt-4 bg-amber-950/30 border border-amber-700/40 rounded-sm p-4" data-testid="2fa-backup-codes">
            <p className="text-sm text-amber-200 font-medium mb-2">⚠️ Save your backup codes — they will only be shown once</p>
            <p className="text-xs text-amber-300/80 mb-3 leading-relaxed">If you lose your authenticator device, you can use one of these codes to recover access.</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {backupCodes.map((c, i) => (
                <code key={i} className="bg-zinc-950 border border-zinc-800 rounded-sm px-2 py-1.5 font-mono text-xs text-zinc-100 text-center">{c}</code>
              ))}
            </div>
          </div>
        )}

        {status?.enabled && !setup && (
          <div className="flex items-center gap-3 mt-2">
            <p className="text-xs text-zinc-500">2FA is enabled. {status.backup_codes_remaining} backup codes remaining.</p>
            <Button onClick={disable} disabled={busy} variant="outline" className="bg-zinc-950 border-red-900/40 hover:bg-red-950/30 text-red-400 rounded-sm" data-testid="2fa-disable">Disable 2FA</Button>
          </div>
        )}
      </div>
    </div>
  );
}

function PrivacySection() {
  const [busy, setBusy] = useState(false);
  const exportData = async () => {
    setBusy(true);
    try {
      // direct download via auth cookie
      window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/gdpr/export`;
      setTimeout(() => setBusy(false), 2000);
    } catch (e) { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="account-privacy-section">
      <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-6">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-10 h-10 rounded-sm bg-[#3377FF]/15 border border-[#3377FF]/40 grid place-items-center">
            <Download className="w-5 h-5 text-[#3377FF]" />
          </div>
          <div className="flex-1">
            <h3 className="font-display font-medium">Export your data (GDPR)</h3>
            <p className="text-xs text-zinc-500 mt-1 leading-relaxed">Download every piece of data we have about your account: projects, audits, blog posts, keyword searches, integrations, subscription history — everything. JSON format.</p>
          </div>
        </div>
        <Button onClick={exportData} disabled={busy} className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm" data-testid="gdpr-export-btn">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />} Download my data
        </Button>
      </div>
    </div>
  );
}

function BillingSection() {
  const { access } = useAuth();
  const [loadingPortal, setLoadingPortal] = useState(false);

  const openPortal = async () => {
    setLoadingPortal(true);
    try {
      const { data } = await api.post("/billing/portal", { return_url: window.location.origin + "/account" });
      window.location.href = data.url;
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not open billing portal");
      setLoadingPortal(false);
    }
  };

  const isSubscribed = access?.status === "active";
  const isUnlimited = access?.unlimited;

  return (
    <div className="space-y-4" data-testid="account-billing-section">
      <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-6">
        <h3 className="font-display font-medium mb-1">Current plan</h3>
        <p className="text-xs text-zinc-500 mb-4">Manage your subscription, payment methods, and invoices.</p>
        <div className="grid sm:grid-cols-2 gap-4 mb-5">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
            <div className="text-[10px] tracking-widest uppercase text-zinc-500 mb-1.5">Status</div>
            <div className="font-display text-lg capitalize">
              {isUnlimited ? <span className="text-[#10B981]">Unlimited</span> :
               access?.status === "active" ? <span className="text-[#10B981]">Active · {access.tier}</span> :
               access?.status === "trial" ? <span className="text-amber-400">Trial · {access.days_left}d left</span> :
               access?.status === "expired" ? <span className="text-red-400">Expired</span> :
               <span className="text-zinc-400">—</span>}
            </div>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
            <div className="text-[10px] tracking-widest uppercase text-zinc-500 mb-1.5">Next renewal</div>
            <div className="font-display text-lg">
              {access?.subscription_ends_at ? new Date(access.subscription_ends_at).toLocaleDateString() :
               access?.trial_ends_at ? new Date(access.trial_ends_at).toLocaleDateString() : "—"}
            </div>
          </div>
        </div>
        <div className="flex gap-3 flex-wrap">
          {isSubscribed && !isUnlimited && (
            <Button onClick={openPortal} disabled={loadingPortal} className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm" data-testid="open-portal-btn">
              {loadingPortal ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-2" />}
              Manage billing (Stripe portal)
            </Button>
          )}
          <Button asChild variant="outline" className="bg-zinc-950 border-zinc-800 rounded-sm">
            <a href="/upgrade">{isSubscribed ? "Change plan" : "Upgrade"}</a>
          </Button>
        </div>
        {!isSubscribed && !isUnlimited && (
          <p className="text-xs text-zinc-500 mt-4 leading-relaxed">
            Subscribe to a paid plan to unlock the Stripe Billing Portal for self-serve invoices, payment method updates, and cancellation.
          </p>
        )}
      </div>
    </div>
  );
}

function ProfileSection({ user, onSaved }) {
  const [name, setName] = useState(user?.name || "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError(""); setSaved(false);
    try {
      await api.patch("/auth/me", { name });
      await onSaved();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e2) { setError(formatApiError(e2)); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="bg-zinc-950 border border-zinc-800 rounded-sm p-6 max-w-xl space-y-4" data-testid="profile-form">
      <div>
        <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Email</Label>
        <Input value={user?.email || ""} disabled
               className="h-11 bg-zinc-900 border-zinc-800 rounded-sm text-zinc-500 cursor-not-allowed" data-testid="profile-email" />
        <p className="text-xs text-zinc-600 mt-1.5">Email is your account identifier and can't be changed here.</p>
      </div>
      <div>
        <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Display name</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} required
               className="h-11 bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#0055FF]" data-testid="profile-name" />
      </div>
      {error && <div className="text-sm text-red-400 border border-red-900/40 bg-red-950/30 px-3 py-2 rounded-sm" data-testid="profile-error">{error}</div>}
      <div className="flex items-center gap-3">
        <Button type="submit" disabled={saving} className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm h-11" data-testid="profile-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Save</>}
        </Button>
        {saved && <span className="text-sm text-[#10B981] flex items-center gap-1" data-testid="profile-saved"><Check className="w-4 h-4" /> Saved</span>}
      </div>
    </form>
  );
}

function PasswordSection({ authProvider }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  if (authProvider !== "email") {
    return (
      <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-8 max-w-xl" data-testid="password-google-only">
        <Lock className="w-6 h-6 text-zinc-600 mb-3" />
        <h3 className="font-display text-lg font-medium mb-2">Managed by Google</h3>
        <p className="text-zinc-400 text-sm leading-relaxed">
          You signed in with Google, so password management happens at <a href="https://myaccount.google.com/security" target="_blank" rel="noreferrer" className="text-[#3377FF] hover:underline">myaccount.google.com/security</a>.
        </p>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSaved(false);
    if (next !== confirm) { setError("New password and confirmation don't match"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      setCurrent(""); setNext(""); setConfirm("");
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e2) { setError(formatApiError(e2)); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="bg-zinc-950 border border-zinc-800 rounded-sm p-6 max-w-xl space-y-4" data-testid="password-form">
      <div>
        <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Current password</Label>
        <PasswordInput value={current} onChange={(e) => setCurrent(e.target.value)} required
               className="h-11 bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#0055FF]" data-testid="password-current" />
      </div>
      <div>
        <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">New password</Label>
        <PasswordInput value={next} onChange={(e) => setNext(e.target.value)} required minLength={6}
               className="h-11 bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#0055FF]" data-testid="password-new" />
        <p className="text-xs text-zinc-600 mt-1.5">Min 6 characters.</p>
      </div>
      <div>
        <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Confirm new password</Label>
        <PasswordInput value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={6}
               className="h-11 bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#0055FF]" data-testid="password-confirm" />
      </div>
      {error && <div className="text-sm text-red-400 border border-red-900/40 bg-red-950/30 px-3 py-2 rounded-sm" data-testid="password-error">{error}</div>}
      <div className="flex items-center gap-3">
        <Button type="submit" disabled={saving} className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm h-11" data-testid="password-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Lock className="w-4 h-4 mr-2" /> Update password</>}
        </Button>
        {saved && <span className="text-sm text-[#10B981] flex items-center gap-1" data-testid="password-saved"><Check className="w-4 h-4" /> Password updated</span>}
      </div>
    </form>
  );
}

function DangerSection({ authProvider, role }) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState(false);

  const isAdmin = role === "admin";

  const remove = async (e) => {
    e.preventDefault();
    setError(""); setDeleting(true);
    try {
      await api.request({
        url: "/auth/me",
        method: "DELETE",
        data: { confirm, ...(authProvider === "email" ? { password } : {}) },
      });
      // ensure local state is cleared
      try { await logout(); } catch {}
      navigate("/", { replace: true });
    } catch (e2) {
      setError(formatApiError(e2));
      setDeleting(false);
    }
  };

  return (
    <div className="bg-red-950/15 border border-red-900/40 rounded-sm p-6 max-w-xl" data-testid="danger-zone">
      <div className="flex items-start gap-3 mb-4">
        <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5" />
        <div>
          <h3 className="font-display text-lg font-medium text-red-100">Delete account</h3>
          <p className="text-sm text-red-200/70 mt-1 leading-relaxed">
            Permanently removes your profile, projects, audits, blogs, content plans, schedules, social channels and all linked data. This action is irreversible.
          </p>
        </div>
      </div>
      {isAdmin ? (
        <div className="text-sm text-amber-300 bg-amber-950/30 border border-amber-900/40 rounded-sm px-3 py-2" data-testid="admin-cannot-delete">
          Admin accounts cannot be self-deleted. Contact platform support.
        </div>
      ) : (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" className="bg-red-950/30 hover:bg-red-950/50 border-red-900/50 text-red-300 hover:text-red-200 rounded-sm" data-testid="delete-account-btn">
              <Trash2 className="w-4 h-4 mr-2" /> Delete my account
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0B0B0E] border-zinc-800 rounded-sm max-w-md" data-testid="delete-dialog">
            <DialogHeader>
              <DialogTitle className="font-display text-red-200 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> This is permanent</DialogTitle>
            </DialogHeader>
            <form onSubmit={remove} className="space-y-4">
              <p className="text-sm text-zinc-400 leading-relaxed">
                All your data — projects, blogs, audits, schedules, social channels and analytics — will be erased. You won't be able to recover any of it.
              </p>
              <div>
                <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Type DELETE to confirm</Label>
                <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} required placeholder="DELETE"
                       className="h-11 bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#EF4444] font-mono" data-testid="delete-confirm-input" />
              </div>
              {authProvider === "email" && (
                <div>
                  <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Confirm password</Label>
                  <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} required
                         className="h-11 bg-zinc-950 border-zinc-800 rounded-sm focus-visible:ring-[#EF4444]" data-testid="delete-password-input" />
                </div>
              )}
              {error && <div className="text-sm text-red-400 border border-red-900/40 bg-red-950/30 px-3 py-2 rounded-sm" data-testid="delete-error">{error}</div>}
              <DialogFooter className="gap-2">
                <Button type="button" onClick={() => setOpen(false)} variant="ghost" className="rounded-sm text-zinc-400" data-testid="delete-cancel">Cancel</Button>
                <Button type="submit" disabled={deleting || confirm !== "DELETE"}
                        className="bg-[#EF4444] hover:bg-red-600 text-white rounded-sm" data-testid="delete-submit">
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete forever"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
      <div className="mt-6 pt-6 border-t border-red-900/30">
        <h4 className="text-sm text-zinc-300 mb-2 font-medium">Sign out everywhere</h4>
        <p className="text-xs text-zinc-500 mb-3">End your session on this device.</p>
        <Button onClick={async () => { await logout(); navigate("/", { replace: true }); }}
                variant="outline" className="bg-zinc-950 hover:bg-zinc-900 border-zinc-800 rounded-sm" data-testid="signout-btn">
          <LogOut className="w-4 h-4 mr-2" /> Sign out
        </Button>
      </div>
    </div>
  );
}
